"use strict";
// Culturally-normed mean-of-responses scorer.
//
// Used when an instrument scores against a reference population rather than
// estimating a latent theta via IRT. The classic example is the SHAW-CAT
// scoring layer used by Promin: alpha drives item selection but does NOT
// enter the scoring step. Instead, the patient's mean response is
// normalised against a per-section (mu, sigma) from the Singapore SHAW
// calibration sample.
//
//   T = clip([1, 99], 50 + 10 × (mean_response - mu) / sigma)
//
// Per-section (mu, sigma) values come from Singapore. Empirical fit from
// the 24-cell Promin parity test:
//   SQ: mu = 2.073, sigma = 0.408   (RMSE 2.5 T over 8 cells)
//   MQ: mu = 1.746, sigma = 0.403   (RMSE 6.7 T — fit shape right,
//                                     aggregation may not be exactly mean)
//   PQ: blocked — no scalar-of-responses formula fits the 3 adaptive cells.
//
// This module ships the math; the per-section (mu, sigma) lookup is
// supplied by the caller (the questionnaire/form-builder layer), pending
// Singapore confirming the authoritative norms.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SHAW_CAT_EMPIRICAL_NORMS = exports.scoreNormedMean = void 0;
/**
 * @typedef {import('./types').Response} Response
 */
/**
 * @typedef {Object} NormedMeanOptions
 * @property {number} mu        Reference-population mean of the raw response
 * @property {number} sigma     Reference-population SD of the raw response
 * @property {[number, number]} [clip]   T-score clip range; default [1, 99]
 */
/**
 * Computes the culturally-normed mean-of-responses T-score.
 *
 * Input responses are expected to already be in clinical direction (0 =
 * worst, K = best). If the source calibration has negative-alpha items,
 * apply `applyClinicalScoreMap` first so that all responses are in a
 * uniform clinical frame before averaging.
 *
 * Returns `null` if there are no usable responses.
 *
 * @param {Response[]} responses
 * @param {NormedMeanOptions} options
 * @returns {number | null}
 */
function scoreNormedMean(responses, options) {
    var _a, _b;
    const { mu, sigma } = options;
    const clip = (_a = options.clip) !== null && _a !== void 0 ? _a : [1, 99];
    if (!Number.isFinite(mu) || !Number.isFinite(sigma) || sigma === 0) {
        throw new Error('scoreNormedMean requires finite numeric mu and non-zero sigma');
    }
    const values = [];
    for (const r of responses) {
        const v = Number((_b = r === null || r === void 0 ? void 0 : r.answer) === null || _b === void 0 ? void 0 : _b.value);
        if (Number.isFinite(v))
            values.push(v);
    }
    if (values.length === 0)
        return null;
    const mean = values.reduce((s, v) => s + v, 0) / values.length;
    const T = 50 + (10 * (mean - mu)) / sigma;
    return Math.max(clip[0], Math.min(clip[1], T));
}
exports.scoreNormedMean = scoreNormedMean;
/**
 * Per-section (mu, sigma) lookup type.
 * @typedef {{ mu: number, sigma: number }} SectionNorm
 */
/**
 * Best-known empirical norms from the 24-cell Promin parity fit.
 *
 * SHAW_CAT_EMPIRICAL_NORMS.SQ is empirically reliable (RMSE 2.5 T).
 *
 * SHAW_CAT_EMPIRICAL_NORMS.MQ has shape right (sigma ~ 0.40 matches SQ)
 * but RMSE 6.7 T suggests the raw-score aggregation is not exactly
 * unweighted mean — pending Singapore disclosing the correct formula.
 *
 * PQ has no entry: no scalar-of-responses formula fits the 3 PQ adaptive
 * cells. Use with caution; consider holding PQ deployment until Singapore
 * provides the authoritative scoring rule.
 *
 * Do not use these values in production without confirmation from
 * Singapore that they match the authoritative SHAW reference norms.
 *
 * @type {Record<string, SectionNorm>}
 */
exports.SHAW_CAT_EMPIRICAL_NORMS = Object.freeze({
    SQ: Object.freeze({ mu: 2.073, sigma: 0.408 }),
    MQ: Object.freeze({ mu: 1.746, sigma: 0.403 }),
    // PQ: pending Singapore — see VALIDATION.md
});
//# sourceMappingURL=normedMean.js.map